// Turns catalog keys and values into words for the user. All defaultValues are English; Dutch comes from the
// translations.
import { classLabel } from "../../pages/finance/classLabel";
import { formatDutchDate } from "../../utils/dutchDate";
import { resolvePath, valueKind, type ResolvedPath } from "./definition";
import type {
  Bucket,
  Catalog,
  CatalogField,
  ClassroomOption,
  ColumnSpec,
  ConditionSpec,
  Display,
  FieldType,
  MeasureSpec,
  ReportSpec,
  ResultColumn,
  SchoolYearOption,
  SummaryGroupSpec,
} from "./types";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type Translate = (key: string, options?: any) => string;

const tr = (t: Translate, key: string, defaultValue: string, options: Record<string, unknown> = {}) =>
  String(t(key, { defaultValue, ...options }));

export function labelOf(t: Translate, item: { labelKey: string; defaultLabel: string }): string {
  return tr(t, item.labelKey, item.defaultLabel);
}

export function sourceLabel(t: Translate, catalog: Catalog, sourceKey: string): string {
  const source = catalog.sources.find((item) => item.key === sourceKey);
  return source ? labelOf(t, source) : sourceKey;
}

/** "Children › Class" for a path, "Children: number" for a counted relation. */
export function pathLabel(t: Translate, resolved: ResolvedPath | null, fallback: string): string {
  if (!resolved) return fallback;
  const relations = resolved.relations.map((relation) => labelOf(t, relation));
  if (!resolved.field) {
    return tr(t, "reports.builder.count_of", "{{name}}: number", { name: relations.join(" › ") });
  }
  return [...relations, labelOf(t, resolved.field)].join(" › ");
}

export function columnHeader(t: Translate, catalog: Catalog, sourceKey: string, column: ColumnSpec): string {
  if (column.label) return column.label;
  return pathLabel(t, resolvePath(catalog, sourceKey, column.path), column.path);
}

export function operatorLabel(t: Translate, operator: string): string {
  const defaults: Record<string, string> = {
    CONTAINS: "contains",
    STARTS_WITH: "starts with",
    EQUALS: "is",
    NOT_EQUALS: "is not",
    GREATER_THAN: "is more than",
    GREATER_OR_EQUAL: "is at least",
    LESS_THAN: "is less than",
    LESS_OR_EQUAL: "is at most",
    ON: "is on",
    BEFORE: "is before",
    AFTER: "is after",
    BETWEEN: "is between",
    LAST_DAYS: "in the last … days",
    NEXT_DAYS: "in the next … days",
    IN_SCHOOL_YEAR: "is in the school year",
    IS_TRUE: "is yes",
    IS_FALSE: "is no",
    ANY_OF: "is one of",
    NONE_OF: "is none of",
    IS_EMPTY: "is empty",
    IS_NOT_EMPTY: "is filled in",
    HAS_ANY: "has at least one",
    HAS_NONE: "has none",
  };
  return tr(t, `reports.builder.operators.${operator}`, defaults[operator] ?? operator);
}

export function choiceLabel(t: Translate, field: CatalogField | null, value: string): string {
  const choice = field?.choices.find((item) => item.value === value);
  return choice ? labelOf(t, choice) : value;
}

export function classroomOptionLabel(t: Translate, option: ClassroomOption): string {
  return classLabel(t, { className: option.className, classType: option.classType, classGrade: option.grade });
}

export function schoolYearLabel(option: SchoolYearOption | null | undefined): string {
  if (!option) return "—";
  const start = option.startDate ? String(option.startDate).slice(0, 4) : "?";
  const end = option.endDate ? String(option.endDate).slice(0, 4) : "?";
  return `${start}–${end}`;
}

/** One value as text. */
export function formatValue(t: Translate, type: FieldType, field: CatalogField | null, value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  switch (type) {
    case "DATE":
      return formatDutchDate(value);
    case "BOOLEAN":
      return value ? tr(t, "common.yes", "Yes") : tr(t, "common.no", "No");
    case "CHOICE":
      return choiceLabel(t, field, String(value));
    case "CLASSROOM": {
      const classroom = value as { className?: string | null; classType?: string | null; grade?: number | null };
      return classLabel(t, { className: classroom.className, classType: classroom.classType, classGrade: classroom.grade })
        || "—";
    }
    default:
      return String(value);
  }
}

/** A period of a summary: 2026 → "2026", school year 2026 → "2026–2027", 202603 → "March 2026". */
export function bucketValue(t: Translate, bucket: Bucket, value: unknown): string {
  if (value === null || value === undefined) return "—";
  const number = Number(value);
  if (bucket === "YEAR") return String(number);
  if (bucket === "SCHOOL_YEAR") return `${number}–${number + 1}`;
  const year = Math.floor(number / 100);
  const month = number % 100;
  const name = tr(t, `reports.builder.months.${month}`, ["January", "February", "March", "April", "May", "June", "July",
    "August", "September", "October", "November", "December"][month - 1] ?? String(month));
  return `${name} ${year}`;
}

export function formatNumber(value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  const number = Number(value);
  if (Number.isNaN(number)) return String(value);
  return number.toLocaleString("nl-NL", { maximumFractionDigits: 2 });
}

/** A cell of a summary: a group value (with its period) or a calculated number. */
export function formatSummaryCell(t: Translate, column: ResultColumn, field: CatalogField | null, value: unknown): string {
  if (column.display === "MEASURE") return formatNumber(value);
  if (column.bucket) return bucketValue(t, column.bucket, value);
  if (!field) return formatNumber(value);
  return formatValue(t, column.type, field, value);
}

/**
 * The values of a list cell, one per related row. Columns from the same relation (a child's name, age and date
 * of birth) come from one query in the same order, so line n of each column belongs to the same child.
 * An empty value stays as "—" so the lines keep lining up.
 */
export function formatListItems(t: Translate, type: FieldType, field: CatalogField | null, value: unknown,
                                more = 0): string[] {
  const items = (Array.isArray(value) ? value : []).map((item) => formatValue(t, type, field, item));
  if (more > 0) items.push(tr(t, "reports.builder.more", "+ {{count}} more", { count: more }));
  return items;
}

/** A cell: a value, a list (joined) or a count. */
export function formatCell(t: Translate, type: FieldType, display: Display, field: CatalogField | null,
                           value: unknown, more = 0): string {
  if (display === "LIST") {
    const items = Array.isArray(value) ? value : [];
    if (items.length === 0) return "—";
    const text = items.map((item) => formatValue(t, type, field, item)).join(", ");
    return more > 0 ? `${text} ${tr(t, "reports.builder.more", "+ {{count}} more", { count: more })}` : text;
  }
  if (display === "COUNT") return String(value ?? 0);
  return formatValue(t, type, field, value);
}

/** Neutral placeholders for the layout: no data is fetched while composing. */
export function sampleCell(t: Translate, type: FieldType, display: Display, row: number): string {
  if (display === "COUNT") return String([3, 1, 2][row % 3]);
  const one = (() => {
    switch (type) {
      case "DATE":
        return tr(t, "reports.builder.sample.date", "dd-mm-yyyy");
      case "NUMBER":
        return "0";
      case "BOOLEAN":
        return tr(t, "reports.builder.sample.boolean", "Yes/No");
      case "CHOICE":
        return tr(t, "reports.builder.sample.choice", "Choice");
      case "CLASSROOM":
        return tr(t, "reports.builder.sample.classroom", "Class");
      default:
        return tr(t, "reports.builder.sample.text", "Text");
    }
  })();
  if (display === "LIST") {
    // One line per related row, as in the result.
    return Array([2, 1, 3][row % 3]).fill(one).join("\n");
  }
  return one;
}

/** "Children › Class is one of Kindergarten Zon" */
export function conditionText(t: Translate, catalog: Catalog, sourceKey: string, condition: ConditionSpec): string {
  const resolved = resolvePath(catalog, sourceKey, condition.path);
  if (resolved && !resolved.field && (condition.operator === "HAS_ANY" || condition.operator === "HAS_NONE")) {
    const name = resolved.relations.map((relation) => labelOf(t, relation)).join(" › ").toLowerCase();
    return condition.operator === "HAS_ANY"
      ? tr(t, "reports.builder.has_any", "Has {{name}}", { name })
      : tr(t, "reports.builder.has_none", "Has no {{name}}", { name });
  }
  const subject = pathLabel(t, resolved, condition.path);
  const operator = operatorLabel(t, condition.operator);
  const value = conditionValueText(t, catalog, resolved, condition);
  return [subject, operator, value].filter(Boolean).join(" ");
}

function conditionValueText(t: Translate, catalog: Catalog, resolved: ResolvedPath | null,
                            condition: ConditionSpec): string {
  const field = resolved?.field ?? null;
  const value = condition.value;
  const one = (item: string | number) => {
    if (field?.type === "CLASSROOM") {
      const option = catalog.classrooms.find((classroom) => classroom.id === String(item));
      return option ? classroomOptionLabel(t, option) : "?";
    }
    if (field?.type === "CHOICE") return choiceLabel(t, field, String(item));
    if (field?.type === "DATE") return formatDutchDate(item);
    return String(item);
  };
  const quoted = field?.type === "TEXT";
  switch (valueKind(condition.operator)) {
    case "NONE":
      return "";
    case "DAYS":
      return tr(t, "reports.builder.days", "{{count}} days", { count: Number(value) });
    case "RANGE":
      return Array.isArray(value)
        ? tr(t, "reports.builder.range", "{{from}} and {{to}}", { from: one(value[0]), to: one(value[1]) })
        : "";
    case "LIST":
      return Array.isArray(value) ? value.map(one).join(", ") : "";
    default:
      if (value === null || value === undefined || Array.isArray(value)) return "";
      return quoted ? `“${one(value)}”` : one(value);
  }
}

/** "Number of children", "Average of Children: number" */
export function measureLabel(t: Translate, catalog: Catalog, sourceKey: string, measure: MeasureSpec): string {
  if (measure.kind === "COUNT" || !measure.path) {
    return tr(t, "reports.builder.measure_count", "Number of {{name}}", {
      name: sourceLabel(t, catalog, sourceKey).toLowerCase(),
    });
  }
  const of = pathLabel(t, resolvePath(catalog, sourceKey, measure.path), measure.path);
  const defaults: Record<string, string> = {
    SUM: "Total of {{name}}", AVG: "Average of {{name}}", MIN: "Lowest {{name}}", MAX: "Highest {{name}}",
  };
  return tr(t, `reports.builder.measures.${measure.kind}`, defaults[measure.kind] ?? measure.kind, { name: of });
}

/** "Registration date (per school year)" */
export function groupLabel(t: Translate, catalog: Catalog, sourceKey: string, group: SummaryGroupSpec): string {
  const base = pathLabel(t, resolvePath(catalog, sourceKey, group.path), group.path);
  if (!group.bucket) return base;
  return `${base} (${bucketName(t, group.bucket)})`;
}

export function bucketName(t: Translate, bucket: Bucket): string {
  const defaults: Record<Bucket, string> = { YEAR: "per year", SCHOOL_YEAR: "per school year", MONTH: "per month" };
  return tr(t, `reports.builder.buckets.${bucket}`, defaults[bucket]);
}

/** Header of a summary column. */
export function summaryHeader(t: Translate, catalog: Catalog, definition: ReportSpec, column: ResultColumn): string {
  const summary = definition.summary;
  const index = Number(column.key.slice(1));
  if (column.display === "GROUP" && summary?.groups[index]) return groupLabel(t, catalog, definition.source, summary.groups[index]);
  if (column.display === "MEASURE" && summary?.measures[index]) {
    return measureLabel(t, catalog, definition.source, summary.measures[index]);
  }
  return column.path ?? column.key;
}

/** "One row per family · School year 2026–2027" */
export function summarySentence(t: Translate, catalog: Catalog, definition: ReportSpec): string {
  const source = catalog.sources.find((item) => item.key === definition.source);
  const parts = [source ? tr(t, source.descriptionKey, source.defaultDescription) : definition.source];
  const year = definition.context.schoolYear.mode === "FIXED"
    ? catalog.schoolYears.find((item) => item.id === definition.context.schoolYear.id)
    : catalog.schoolYears.find((item) => item.id === catalog.currentSchoolYearId);
  if (definition.summary) {
    parts[0] = tr(t, "reports.builder.summary_of", "Summary of {{name}}", {
      name: sourceLabel(t, catalog, definition.source).toLowerCase(),
    });
  }
  parts.push(definition.context.schoolYear.mode === "FIXED"
    ? tr(t, "reports.builder.school_year_fixed", "School year {{year}}", { year: schoolYearLabel(year) })
    : tr(t, "reports.builder.school_year_current", "Current school year ({{year}})", { year: schoolYearLabel(year) }));
  return parts.join(" · ");
}
